<script setup lang="ts">
import { computed, onBeforeUnmount, ref, watch } from 'vue'
import Button from 'primevue/button'
import Dialog from 'primevue/dialog'
import { createAgentSetup, type AgentSetup } from '@/api/agentSetup'

const visible = defineModel<boolean>('visible', { default: false })
const setup = ref<AgentSetup | null>(null)
const busy = ref(false)
const error = ref('')
const copied = ref(false)
const now = ref(Date.now())
let timer: ReturnType<typeof setInterval> | undefined
let requestId = 0
const expired = computed(() => !!setup.value && now.value >= Date.parse(setup.value.expires_at))
const expiresLabel = computed(() => setup.value ? new Date(setup.value.expires_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '')

async function generate(): Promise<void> {
  const id = ++requestId
  busy.value = true
  error.value = ''
  copied.value = false
  setup.value = null
  try {
    const result = await createAgentSetup()
    if (id === requestId && visible.value) setup.value = result
  } catch {
    if (id === requestId) error.value = 'Не удалось получить ссылку. Попробуйте ещё раз.'
  } finally {
    if (id === requestId) busy.value = false
  }
}
async function copy(): Promise<void> {
  if (!setup.value || expired.value) return
  try {
    await navigator.clipboard.writeText(setup.value.instruction)
    copied.value = true
    error.value = ''
  } catch {
    error.value = 'Автоматическое копирование недоступно. Выделите и скопируйте текст вручную.'
  }
}
watch(visible, (open) => {
  clearInterval(timer)
  if (open) {
    now.value = Date.now()
    timer = setInterval(() => { now.value = Date.now() }, 1000)
    void generate()
  } else {
    ++requestId
    setup.value = null
    busy.value = false
  }
})
onBeforeUnmount(() => { ++requestId; clearInterval(timer) })
</script>

<template>
  <Dialog v-model:visible="visible" modal header="Настроить агента" :style="{ width: '38rem', maxWidth: '95vw' }">
    <p>Скопируйте сообщение и отправьте своему агенту. Он установит скилы для работы с Motiga.</p>
    <p v-if="busy" role="status">Готовим ссылку…</p>
    <textarea v-if="setup" class="agent-instruction" aria-label="Сообщение для агента" readonly :value="setup.instruction" @focus="($event.target as HTMLTextAreaElement).select()" />
    <p v-if="setup" role="status">{{ expired ? 'Ссылка истекла. Получите новую.' : `Ссылка действует до ${expiresLabel}. Вход в аккаунт настраивается отдельно.` }}</p>
    <p v-if="error" role="alert">{{ error }}</p>
    <template #footer>
      <Button label="Обновить ссылку" severity="secondary" :disabled="busy" @click="generate" />
      <Button :label="copied ? 'Скопировано' : 'Копировать'" :icon="copied ? 'pi pi-check' : 'pi pi-copy'" :disabled="busy || !setup || expired" @click="copy" />
    </template>
  </Dialog>
</template>

<style scoped>
.agent-instruction { width: 100%; min-height: 14rem; box-sizing: border-box; resize: vertical; padding: 0.75rem; font: inherit; color: inherit; background: var(--p-content-background); border: 1px solid var(--p-content-border-color); border-radius: 0.5rem; }
</style>
